import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';

class ProfileServiceGen2 {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('ProfileService');

    this.client = generateClient();
    this.cache = new Map();
    this.subscriptions = new Map();
    this.retryAttempts = 3;
    this.retryDelay = 1000;
  }

  /**
   * Get user profile by ID
   * @param {string} profileId - Profile ID
   * @returns {Promise<Object>} User profile data
   */
  async getProfile(profileId) {
    if (this.cache.has(profileId)) {
      return this.cache.get(profileId);
    }

    try {
      const { data: profile } = await this.client.models.UserProfile.get({
        id: profileId
      });

      if (profile) {
        this.cache.set(profileId, profile);
      }
      return profile;
    } catch (error) {
      this.logger.error('Error fetching profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile by user ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User profile data
   */
  async getProfileByUserId(userId) {
    try {
      const { data: profiles } = await this.client.models.UserProfile.list({
        filter: {
          userId: { eq: userId }
        }
      });

      return profiles.length > 0 ? profiles[0] : null;
    } catch (error) {
      this.logger.error('Error fetching profile by user ID:', error);
      throw error;
    }
  }

  /**
   * Create a new user profile
   * @param {Object} profileData - Profile data
   * @returns {Promise<Object>} Created profile
   */
  async createProfile(profileData) {
    try {
      const currentUser = await getCurrentUser();
      const userId = currentUser.userId;

      const input = {
        ...profileData,
        userId,
      };

      const { data: createdProfile } = await this.client.models.UserProfile.create(input);

      if (createdProfile) {
        this.cache.set(createdProfile.id, createdProfile);
      }
      return createdProfile;
    } catch (error) {
      this.logger.error('Error creating profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   * @param {string} profileId - Profile ID
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} Updated profile
   */
  async updateProfile(profileId, profileData) {
    try {
      const currentUser = await getCurrentUser();
      const userId = currentUser.userId;

      // Get current profile for change detection
      const currentProfile = await this.getProfile(profileId);
      
      // Log changes for audit
      await this.logProfileChanges(userId, currentProfile, profileData);

      const input = {
        id: profileId,
        ...profileData,
      };

      const { data: updatedProfile } = await this.client.models.UserProfile.update(input);

      if (updatedProfile) {
        this.cache.set(profileId, updatedProfile);
      }
      return updatedProfile;
    } catch (error) {
      this.logger.error('Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Delete user profile
   * @param {string} profileId - Profile ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteProfile(profileId) {
    try {
      const { data: deletedProfile } = await this.client.models.UserProfile.delete({
        id: profileId
      });

      this.cache.delete(profileId);
      return deletedProfile;
    } catch (error) {
      this.logger.error('Error deleting profile:', error);
      throw error;
    }
  }

  /**
   * Log profile changes for audit
   * @param {string} userId - User ID
   * @param {Object} oldData - Previous profile data
   * @param {Object} newData - New profile data
   */
  async logProfileChanges(userId, oldData, newData) {
    try {
      const changes = this.detectChanges(oldData, newData);
      
      for (const change of changes) {
        await this.client.models.ProfileAuditLog.create({
          userId,
          fieldChanged: change.field,
          oldValue: change.oldValue,
          newValue: change.newValue,
          changedAt: new Date().toISOString(),
          ipAddress: await this.getClientIP(),
          userAgent: navigator.userAgent
        });
      }
    } catch (error) {
      this.logger.error('Error logging profile changes:', error);
      // Don't throw error to avoid breaking the main operation
    }
  }

  /**
   * Detect changes between old and new profile data
   * @param {Object} oldData - Previous data
   * @param {Object} newData - New data
   * @returns {Array} Array of changes
   */
  detectChanges(oldData, newData) {
    const changes = [];
    const fieldsToTrack = [
      'firstName', 'lastName', 'company', 'phone', 
      'bio', 'website', 'linkedinUrl', 'profilePicture'
    ];

    fieldsToTrack.forEach(field => {
      const oldValue = oldData?.[field] || '';
      const newValue = newData?.[field] || '';
      
      if (oldValue !== newValue) {
        changes.push({
          field,
          oldValue: oldValue.toString(),
          newValue: newValue.toString()
        });
      }
    });

    // Check address changes
    if (oldData?.address || newData?.address) {
      const addressFields = ['street', 'city', 'state', 'zipCode', 'country'];
      addressFields.forEach(field => {
        const oldValue = oldData?.address?.[field] || '';
        const newValue = newData?.address?.[field] || '';
        
        if (oldValue !== newValue) {
          changes.push({
            field: `address.${field}`,
            oldValue: oldValue.toString(),
            newValue: newValue.toString()
          });
        }
      });
    }

    return changes;
  }

  /**
   * #433 — ask Amazon SES to verify an advisor's own-domain reply address.
   * Goes through the `requestReplyEmailVerification` custom mutation (handled
   * by amplify/functions/reply-email-verification); the browser never holds
   * SES permissions. Resolves to { email, status, platform, checkedAt }.
   */
  async requestReplyEmailVerification(email) {
    const normalized = this._normalizeReplyEmail(email);
    const { data, errors } = await this.client.mutations.requestReplyEmailVerification({ email: normalized });
    if (errors?.length) {
      throw new Error(errors[0]?.message || 'Reply email verification request failed');
    }
    return data;
  }

  /** #433 — current SES verification status for a reply address (no side effects). */
  async getReplyEmailVerification(email) {
    const normalized = this._normalizeReplyEmail(email);
    const { data, errors } = await this.client.queries.getReplyEmailVerification({ email: normalized });
    if (errors?.length) {
      throw new Error(errors[0]?.message || 'Reply email verification lookup failed');
    }
    return data;
  }

  _normalizeReplyEmail(email) {
    const normalized = typeof email === 'string' ? email.trim().toLowerCase() : '';
    if (!normalized) {
      throw new Error('A reply email address is required');
    }
    return normalized;
  }

  /**
   * Get client IP address (simplified implementation)
   * @returns {Promise<string>} Client IP
   */
  async getClientIP() {
    // In a real implementation, you might call an external service
    // For now, we'll return a placeholder
    return 'client-ip-placeholder';
  }

  /**
   * Get profile audit logs
   * @param {string} userId - User ID
   * @param {number} limit - Number of logs to fetch
   * @returns {Promise<Array>} Audit logs
   */
  async getAuditLogs(userId, limit = 50) {
    try {
      const { data: auditLogs } = await this.client.models.ProfileAuditLog.list({
        filter: {
          userId: { eq: userId }
        },
        limit
      });

      return auditLogs;
    } catch (error) {
      this.logger.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  /**
   * Subscribe to profile changes (Gen 2 real-time feature)
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToProfileChanges(userId, callback) {
    return this.client.models.UserProfile.observeQuery({
      filter: {
        userId: { eq: userId }
      }
    }).subscribe({
      next: ({ items }) => callback(items),
      error: (error) => this.logger.error('Subscription error:', error)
    });
  }

  /**
   * Subscribe to audit log changes (Gen 2 real-time feature)
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToAuditLogs(userId, callback) {
    return this.client.models.ProfileAuditLog.observeQuery({
      filter: {
        userId: { eq: userId }
      }
    }).subscribe({
      next: ({ items }) => callback(items),
      error: (error) => this.logger.error('Subscription error:', error)
    });
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Create singleton instance
const profileServiceInstance = new ProfileServiceGen2();

// Check feature flag for singleton behavior
const USE_SINGLETON = import.meta.env?.VITE_USE_PROFILE_SINGLETON !== 'false' &&
                      process.env.REACT_APP_USE_PROFILE_SINGLETON !== 'false';

// Export based on feature flag
export default USE_SINGLETON ? profileServiceInstance : ProfileServiceGen2;

// Always export the class for testing
export { ProfileServiceGen2 };
